import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async (event) => {
    // The drawer's cart-items-component lives inside the HEADER section. When the drawer dialog is
    // already open the section morph skips it, and (CH-186) the sections bundled in the add response
    // proved unreliable on back-to-back adds — the drawer froze on the first-added line. So we
    // re-fetch the section FRESH from the server and rebuild the drawer BEFORE showing it.
    await this.#refresh(event);
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    this.#showAddedItem(
      event?.detail?.data?.productId,
      event?.detail?.data?.sellingPlanId,
      event?.detail?.data?.lineKey,
      0
    );
  };

  #refresh = async (event) => {
    const container = this.querySelector('.cart-items-component');
    const sectionId = container?.dataset.sectionId;
    if (!container || !sectionId) return;
    // CH-186: fetch the section fresh so the open drawer always reflects the CURRENT cart. This is
    // the reliable fix for the "drawer keeps the first-added item" bug — the just-added line (and its
    // data-line-key) is guaranteed present, so #showAddedItem can pin it.
    let html = null;
    try {
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (res.ok) html = await res.text();
    } catch (e) {
      /* fall through to the add-response sections */
    }
    if (!html) html = event?.detail?.data?.sections?.[sectionId];
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-items-component');
    if (fresh) container.innerHTML = fresh.innerHTML;
  };

  #showAddedItem = (productId, sellingPlanId, lineKey, attempt) => {
    const items = [...this.querySelectorAll('.nad-notif__item')];
    // CH-186: prefer the UNIQUE cart line key (data-line-key) — this pins the exact just-added line
    // even when the same product sits under several plans/quantities, fixing the "drawer keeps the
    // first-added item" bug (only the .nad-notif__item--active row is visible). Fall back to CH-174's
    // productId + selling-plan match if the key isn't available.
    const wantPlan = sellingPlanId != null ? String(sellingPlanId) : '';
    let matched = null;
    if (lineKey) matched = items.find((item) => item.dataset.lineKey === String(lineKey));
    if (!matched && productId) {
      matched = items.find(
        (item) => item.dataset.productId === String(productId) && (item.dataset.sellingPlan || '') === wantPlan
      );
    }
    const wantMatch = lineKey || productId;
    if (!items.length || (wantMatch && !matched && attempt < 12)) {
      setTimeout(() => this.#showAddedItem(productId, sellingPlanId, lineKey, attempt + 1), 80);
      return;
    }
    const active = matched || items[items.length - 1];
    items.forEach((item) => item.classList.toggle('nad-notif__item--active', item === active));
    const title = this.querySelector('[data-nad-notif-title]');
    if (title && active?.dataset.notifHeader) title.textContent = active.dataset.notifHeader;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
